
import React, { useState } from "react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calculator, Droplets, Gauge, Settings, RotateCcw } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CALIBRATION_KEYS, PREFERENCE_KEYS, CalibrationMetadata } from "@/constants/calibrationKeys";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Tools = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [refreshKey, setRefreshKey] = useState(0);

  const getCalibrationData = (key: string): CalibrationMetadata | null => {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  };

  const resetAllProgress = () => {
    Object.values(CALIBRATION_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    toast({
      title: "Progress Reset",
      description: "All calibration progress has been cleared.",
    });
    // Trigger re-render instead of page reload
    setRefreshKey(prev => prev + 1);
  };

  const handleSlicerChange = (value: string) => {
    if (value === "clear") {
      localStorage.removeItem(PREFERENCE_KEYS.slicer);
      toast({
        title: "Slicer Preference Cleared",
        description: "Your preferred slicer has been cleared.",
      });
    } else {
      localStorage.setItem(PREFERENCE_KEYS.slicer, value);
      const slicerNames = {
        creality: "Creality Print",
        orca: "Orca Slicer",
        anycubic: "Anycubic Next"
      };
      toast({
        title: "Slicer Preference Set",
        description: `${slicerNames[value as keyof typeof slicerNames]} has been set as your preferred slicer.`,
      });
    }
    setRefreshKey(prev => prev + 1);
  };

  const getCurrentSlicer = () => {
    const saved = localStorage.getItem(PREFERENCE_KEYS.slicer);
    return saved || "";
  };

  const flowCalibrationData = getCalibrationData(CALIBRATION_KEYS.flow);
  const pressureAdvanceCalibrationData = getCalibrationData(CALIBRATION_KEYS.pressureAdvance);

  const calculators = [
    {
      id: "flow-rate",
      title: "Flow Rate / Flow Ratio Calculator",
      description: "Calculate optimal flow rate for your 3D printer based on nozzle diameter, layer height, and print speed.",
      icon: <Droplets className="h-8 w-8 text-primary" />,
      status: "Available",
      href: "/calculators/flow-rate",
      isCompleted: flowCalibrationData?.completed || false
    },
    {
      id: "pressure-advance",
      title: "Pressure Advance Calculator",
      description: "Calculate optimal pressure advance settings to improve print quality and reduce artifacts.",
      icon: <Settings className="h-8 w-8 text-primary" />,
      status: "Available",
      href: "/calculators/pressure-advance",
      isCompleted: pressureAdvanceCalibrationData?.completed || false
    },
    {
      id: "max-volumetric-flow",
      title: "Maximum Volumetric Flow Rate Calculator",
      description: "Determine the maximum volumetric flow rate your hotend can handle for different materials.",
      icon: <Gauge className="h-8 w-8 text-primary" />,
      status: "Coming Soon",
      href: null,
      isCompleted: false
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background" key={refreshKey}>
      <Helmet>
        <title>3DP Hub Tools - 3D Printing Calculators and Utilities</title>
        <meta name="description" content="Essential 3D printing calculators and tools including flow rate, volumetric flow, and pressure advance calculators for optimal print settings." />
        <meta name="keywords" content="3D printing tools, flow rate calculator, volumetric flow calculator, pressure advance, 3D printer calibration, print settings" />
        <link rel="canonical" href="https://3dphub.net/tools" />
        <meta property="og:title" content="3DP Hub Tools - 3D Printing Calculators" />
        <meta property="og:description" content="Essential calculators and tools for 3D printing optimization and calibration." />
        <meta property="og:url" content="https://3dphub.net/tools" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header 
        pageName="Tools"
        useBackButton={true}
      />
      
      <div className="container max-w-6xl py-6 pt-[64px]">
        <div className="flex flex-col gap-4 mb-8">
          <div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">3D printer calibration calculators</h1>
                <p className="text-muted-foreground">
                  Essential calculators and utilities to help optimize your 3D printing settings and improve print quality.
                </p>
              </div>
              {!isMobile && (
                <div className="flex gap-2">
                  <Select onValueChange={handleSlicerChange} value={getCurrentSlicer()}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select Slicer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="creality">Creality Print</SelectItem>
                      <SelectItem value="orca">Orca Slicer</SelectItem>
                      <SelectItem value="anycubic">Anycubic Next</SelectItem>
                      <SelectItem value="clear">Clear Selection</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={resetAllProgress}
                    variant="outline"
                    size="sm"
                    className="self-start"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset Progress
                  </Button>
                </div>
              )}
            </div>
            {isMobile && (
              <div className="flex gap-2 mt-4">
                <Select onValueChange={handleSlicerChange} value={getCurrentSlicer()}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select Slicer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="creality">Creality Print</SelectItem>
                    <SelectItem value="orca">Orca Slicer</SelectItem>
                    <SelectItem value="anycubic">Anycubic Next</SelectItem>
                    <SelectItem value="clear">Clear Selection</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={resetAllProgress}
                  variant="outline"
                  size="sm"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Progress
                </Button>
              </div>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {calculators.map((calculator) => (
            <Card 
              key={calculator.id} 
              className={`h-full hover:shadow-md border border-border ${
                calculator.isCompleted 
                  ? 'bg-accent border-primary' 
                  : ''
              }`}
              title={calculator.isCompleted ? "Calibration completed" : ""}
            >
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    {calculator.icon}
                  </div>
                  <div className="flex-1">
                    <div className="h-12 flex items-start">
                      <CardTitle className="text-lg leading-tight">{calculator.title}</CardTitle>
                    </div>
                    <CardDescription>{calculator.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  {calculator.isCompleted && (
                    <Badge variant="completed">
                      Completed
                    </Badge>
                  )}
                  {!calculator.isCompleted && <div></div>}
                  {calculator.href ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      asChild
                    >
                      <Link to={calculator.href}>
                        <Calculator className="h-4 w-4 mr-2" />
                        Launch Tool
                      </Link>
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      disabled
                    >
                      <Calculator className="h-4 w-4 mr-2" />
                      Launch Tool
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="bg-muted rounded-lg p-6 border border-border">
          <h2 className="text-xl font-semibold mb-3">More Tools Coming Soon</h2>
          <p className="text-muted-foreground mb-4">
            We're actively developing more calculators and utilities to help the 3D printing community. 
            These tools will include advanced calibration calculators, material property converters, 
            and performance optimization utilities.
          </p>
          <p className="text-muted-foreground">
            Have an idea for a tool that would be helpful? Let us know through our feature suggestion system!
          </p>
        </div>
      </div>
      
      <footer className="bg-card border-t border-border py-6 mt-auto">
        <div className="container">
          <p className="text-sm text-center text-muted-foreground">
            3DP Hub | The ultimate directory for 3D printer parts and upgrades
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Tools;
